import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { CognitoUser } from '@aws-amplify/auth';
import { User } from '@/types';
import { apiClient } from '@/utils/api-client';

interface AuthContextType {
  user: CognitoUser | null;
  userProfile: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  // New role checking utilities
  isGuest: () => boolean;
  isHost: () => boolean;
  canBecomeHost: () => boolean;
  getUserState: () => 'guest-only' | 'guest-and-host';
  upgradeToHost: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Role checking utilities
  const isGuest = useCallback(() => {
    return userProfile?.roles.includes('guest') || false;
  }, [userProfile]);

  const isHost = useCallback(() => {
    return userProfile?.roles.includes('host') || false;
  }, [userProfile]);

  const canBecomeHost = useCallback((): boolean => {
    return Boolean(userProfile?.roles.includes('guest') && !userProfile?.roles.includes('host'));
  }, [userProfile]);

  const getUserState = useCallback((): 'guest-only' | 'guest-and-host' => {
    if (!userProfile) return 'guest-only';
    const hasGuest = userProfile.roles.includes('guest');
    const hasHost = userProfile.roles.includes('host');
    
    if (hasGuest && hasHost) return 'guest-and-host';
    return 'guest-only';
  }, [userProfile]);



  const checkAuthState = useCallback(async () => {
    try {
      setError(null);
      const currentUser = await Auth.currentAuthenticatedUser();
      setUser(currentUser);
      
      // Get user attributes to build profile
      const attributes = await Auth.userAttributes(currentUser);
      const attributeMap = attributes.reduce((acc, attr) => {
        acc[attr.Name] = attr.Value;
        return acc;
      }, {} as Record<string, string>);

      // Parse roles and authentication methods
      const rolesString = attributeMap['custom:user_roles'] || '["guest"]';
      const roles = JSON.parse(rolesString) as ('host' | 'guest')[];
      const primaryRole = (attributeMap['custom:primary_role'] as 'host' | 'guest') || 'guest';
      
      const authMethodsString = attributeMap['custom:auth_methods'] || '[]';
      const authMethods = JSON.parse(authMethodsString) as any[];

      const profile: User = {
        userId: currentUser.username,
        email: attributeMap.email || undefined,
        phoneNumber: attributeMap.phone_number || undefined,
        name: `${attributeMap.given_name || ''} ${attributeMap.family_name || ''}`.trim(),
        given_name: attributeMap.given_name || undefined,
        family_name: attributeMap.family_name || undefined,
        bio: attributeMap['custom:bio'] || undefined,
        photoUrl: attributeMap['custom:photoUrl'] || undefined,
        
        // Dual role support
        roles,
        primaryRole,
        userGroup: (attributeMap['custom:user_group'] as 'hosts' | 'guests' | 'employees') || 'guests',
        
        // Authentication methods
        authenticationMethods: authMethods,
        
        // Role-specific profiles (will be populated from backend if needed)
        guestProfile: roles.includes('guest') ? {
          checkInHistory: [],
          documentVerificationStatus: 'pending',
          communicationPreferences: {
            marketing: true,
            bookingUpdates: true,
            reminders: true,
            hostMessages: true,
            optOutAll: false
          }
        } : undefined,
        
        hostProfile: roles.includes('host') ? {
          communicationHooks: [],
          defaultCheckInRequirements: {
            requireId: true,
            requireEmergencyContact: false,
            requireDietaryInfo: false,
            customFields: []
          },
          verificationLevel: 'basic'
        } : undefined,
        
        createdAt: attributeMap['custom:createdAt'] || new Date().toISOString(),
        updatedAt: attributeMap['custom:updatedAt'] || new Date().toISOString(),
      };

      setUserProfile(profile);
      console.log('Auth: User authenticated successfully', { userId: profile.userId, userGroup: profile.userGroup });
    } catch (error: any) {
      // User is not authenticated
      setUser(null);
      setUserProfile(null);
      console.log('Auth: User not authenticated', error.code || error.name);
      
      // Clear API client token cache when user is not authenticated
      apiClient.clearTokenCache();
      
      // Only set error if it's not just "not authenticated"
      if (error.code !== 'NotAuthorizedException' && error.name !== 'NotAuthorizedException') {
        setError(error.message || 'Authentication error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    await checkAuthState();
  }, [checkAuthState]);

  const upgradeToHost = useCallback(async () => {
    if (!user || !userProfile) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);
      
      // Add user to hosts Cognito group while keeping them in guests group
      // This will be handled by the backend API
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      
      const response = await fetch('https://apis.bnbcart.com/profile/upgrade-to-host', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to upgrade to host');
      }

      // Force complete token refresh to get updated groups
      // When a user is added to a new Cognito group, they need fresh tokens
      try {
        // Clear all caches first
        apiClient.clearTokenCache();
        
        // Get current user and force refresh session
        const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
        
        // Get fresh session with updated groups
        const freshSession = await Auth.currentSession();
        
        console.log('Token refreshed successfully after host upgrade');
        
        // Small delay to ensure token propagation
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If automatic refresh fails, inform user they may need to sign out/in
        console.warn('Automatic token refresh failed. User may need to sign out and back in for full host access.');
      }
      
      // Refresh user data with the new token
      await refreshUser();
    } catch (error: any) {
      console.error('Host upgrade error:', error);
      setError(error.message || 'Failed to upgrade to host');
      throw error;
    }
  }, [user, userProfile, refreshUser]);

  const signOut = useCallback(async () => {
    try {
      setError(null);
      await Auth.signOut();
      setUser(null);
      setUserProfile(null);
      
      // Clear API client token cache
      apiClient.clearTokenCache();
    } catch (error: any) {
      console.error('Error signing out:', error);
      setError(error.message || 'Sign out failed');
    }
  }, []);

  useEffect(() => {
    checkAuthState();

    // Listen for auth events
    const hubListener = (data: any) => {
      const { payload } = data;
      
      switch (payload.event) {
        case 'signIn':
          console.log('Auth event: signIn');
          checkAuthState();
          break;
        case 'signOut':
          console.log('Auth event: signOut');
          setUser(null);
          setUserProfile(null);
          apiClient.clearTokenCache();
          break;
        case 'tokenRefresh':
          console.log('Auth event: tokenRefresh');
          // Don't need to checkAuthState on token refresh, just clear cache
          apiClient.clearTokenCache();
          break;
        case 'tokenRefresh_failure':
          console.log('Auth event: tokenRefresh_failure');
          setError('Session expired. Please sign in again.');
          setUser(null);
          setUserProfile(null);
          apiClient.clearTokenCache();
          break;
        default:
          break;
      }
    };

    Hub.listen('auth', hubListener);

    return () => {
      Hub.remove('auth', hubListener);
    };
  }, [checkAuthState]);

  const value: AuthContextType = {
    user,
    userProfile,
    isLoading,
    isAuthenticated: !!user,
    error,
    signOut,
    refreshUser,
    clearError,
    isGuest,
    isHost,
    canBecomeHost,
    getUserState,
    upgradeToHost,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;